import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  Upload,
  FileText,
  Link as LinkIcon,
  Robot,
  MagnifyingGlass,
  DotsThreeVertical,
  DownloadSimple,
  Trash,
  Eye,
  ArrowsClockwise,
  WarningCircle,
  CheckCircle,
  Clock,
  X,
  Plus,
  PencilSimple,
  Check,
  Folder,
  FolderOpen,
  FilePdf,
  FileDoc,
  FilePpt,
  Globe,
  Sparkle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { MaterialCardSkeleton } from '@/components/ui/Skeleton'
import { materialsApi, getErrorMessage } from '@/lib/api'
import { usePermission } from '@/hooks/usePermission'
import { useTrialStatus } from '@/hooks/useTrialStatus'
import { planLimitToastMessage } from '@/lib/planLimitMessages'
import { sanitizeMaterialSummary } from '@/lib/insights'
import { formatBytes, formatDate, cn } from '@/lib/utils'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import { toast } from 'sonner'
import { MaterialPreviewDrawer } from './components/MaterialPreviewDrawer'
import { GenerateDeckModal } from './components/GenerateDeckModal'
import { downloadMaterial } from './downloadMaterial'

// Types
interface Material {
  id: number
  title: string
  description?: string
  material_type: string
  content_type: string
  file_name?: string
  file_path?: string
  file_size?: number
  url?: string
  processing_status: 'pending' | 'processing' | 'crawling' | 'indexing' | 'completed' | 'failed'
  processing_error?: string | null
  embedding_status?: 'pending' | 'processing' | 'completed' | 'failed' | null
  created_at: string
  updated_at: string
  usage_count: number
  tags?: string[]
  category?: string
  ai_summary?: string
  key_points?: string[]
  pageindex_doc_id?: string
  crawl_pages_found?: number
  // AI-generated deck provenance (null/undefined = uploaded material)
  generation_source?: 'ai_deck' | null
  // Merge tokens the deck was generated with (filled per recipient at send
  // time); non-empty means the stored file contains literal {{...}} fields.
  personalization_tokens?: string[] | null
}

// File upload validation constants
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 MB
const ALLOWED_FILE_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
}
const ALLOWED_EXTENSIONS = Object.values(ALLOWED_FILE_TYPES).flat()

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
    return `File is too large (${sizeMB} MB). Maximum size is 20 MB.`
  }
  const ext = '.' + file.name.split('.').pop()?.toLowerCase()
  const mimeOk = Object.keys(ALLOWED_FILE_TYPES).includes(file.type)
  const extOk = ALLOWED_EXTENSIONS.includes(ext)
  if (!mimeOk && !extOk) {
    return `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`
  }
  return null
}

// Upload Modal Component

const UploadModal: React.FC<{ isOpen: boolean; onClose: () => void; onUpload: () => void }> = ({
  isOpen,
  onClose,
  onUpload
}) => {
  const [uploadType, setUploadType] = useState<'file' | 'url'>('file')
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [dragActive, setDragActive] = useState(false)
  // Staged files with editable titles
  const [stagedFiles, setStagedFiles] = useState<{ file: File; title: string }[]>([])
  // User-chosen tags applied to everything in this upload batch
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isTrial } = useTrialStatus()
  // The Upgrade action only appears for whoever can actually act on it:
  // billing.manage_subscription is Owner/Admin, and Billing is hidden in
  // the nav for everyone else, so the button led to a page that exists
  // only to say billing is not theirs. The message itself still explains
  // the cap, which is the part every role needs.
  const { canWrite: canUpgradePlan } = usePermission('billing', 'manage_subscription')

  const resetState = () => {
    setUrl('')
    setTitle('')
    setStagedFiles([])
    setTags([])
    setTagInput('')
  }

  const addTag = () => {
    const t = tagInput.trim().replace(/,+$/, '').trim()
    setTagInput('')
    if (!t) return
    setTags(prev =>
      prev.some(existing => existing.toLowerCase() === t.toLowerCase()) ? prev : [...prev, t]
    )
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = Array.from(e.dataTransfer.files)
    stageFiles(files)
  }, [])

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const stageFiles = (files: File[]) => {
    const valid: { file: File; title: string }[] = []
    for (const file of files) {
      const err = validateFile(file)
      if (err) {
        toast.error(`${file.name}: ${err}`)
      } else {
        // Default title = filename without extension
        const nameWithoutExt = file.name.replace(/\.[^.]+$/, '')
        valid.push({ file, title: nameWithoutExt })
      }
    }
    if (valid.length > 0) {
      setStagedFiles(prev => [...prev, ...valid])
    }
  }

  const handleUploadStaged = () => {
    if (stagedFiles.length === 0) return

    const filesToUpload = [...stagedFiles]
    const count = filesToUpload.length

    // Close modal immediately, uploads happen in the background.
    setStagedFiles([])
    handleClose()
    toast.info(`Uploading ${count} file${count > 1 ? 's' : ''} in the background...`)

    // Fire-and-forget async upload. Exactly one terminal toast summarizes the
    // whole batch (no per-file toasts), matching the single-notification model.
    ;(async () => {
      let succeeded = 0
      let failed = 0
      // Once the trial's material cap is confirmed hit, further attempts in
      // this batch would only fail the same way — stop trying and surface
      // ONE clear message instead of a wall of identical per-file failures.
      let planLimitError: unknown = null
      for (const { file, title: fileTitle } of filesToUpload) {
        if (planLimitError) { failed++; continue }
        try {
          await materialsApi.upload(file, fileTitle.trim() || undefined, tags)
          succeeded++
          queryClient.invalidateQueries({ queryKey: ['materials'] })
        } catch (error: any) {
          const detail = error?.response?.data?.detail
          if (typeof detail === 'string' && detail.toLowerCase().includes('plan')) {
            planLimitError = error
          }
          failed++
        }
      }
      if (succeeded > 0) onUpload()
      if (planLimitError) {
        const { message, showUpgrade } = planLimitToastMessage(planLimitError, { isTrial, fallback: 'Failed to upload material' })
        toast.error(message, showUpgrade && canUpgradePlan ? { action: { label: 'Upgrade', onClick: () => navigate('/billing') } } : undefined)
      } else if (failed === 0) {
        toast.success(`${succeeded} file${succeeded > 1 ? 's' : ''} uploaded. Processing in the background.`)
      } else if (succeeded === 0) {
        toast.error(`Couldn't upload ${failed === 1 ? 'the file' : `all ${failed} files`}. Please try again.`)
      } else {
        toast.warning(`${succeeded} of ${count} files uploaded. ${failed} could not be uploaded.`)
      }
    })()
  }

  const handleUrlUpload = () => {
    const uploadUrl = url.trim()
    if (!uploadUrl) return

    // Validate the URL client-side (mirrors the backend's http(s) check) so an
    // invalid link shows a single error right here, with no network round-trip
    // and no optimistic "importing" toast.
    let isValidHttpUrl = false
    try {
      const parsed = new URL(uploadUrl)
      isValidHttpUrl = parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      isValidHttpUrl = false
    }
    if (!isValidHttpUrl) {
      toast.error('Please enter a valid URL starting with http:// or https://')
      return
    }

    const uploadTitle = title.trim() || undefined

    // Close modal immediately
    handleClose()
    toast.info(`Importing from the URL in the background...`)

    ;(async () => {
      try {
        await materialsApi.uploadUrl({ url: uploadUrl, title: uploadTitle, tags })
        queryClient.invalidateQueries({ queryKey: ['materials'] })
        onUpload()
        toast.success('URL imported. Processing in the background.')
      } catch (error: unknown) {
        const { message, showUpgrade } = planLimitToastMessage(error, { isTrial, fallback: getErrorMessage(error, 'URL upload failed') })
        toast.error(message, showUpgrade && canUpgradePlan ? { action: { label: 'Upgrade', onClick: () => navigate('/billing') } } : undefined)
      }
    })()
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Upload materials"
    >
      {/* Backdrop — blur-only, no dark overlay (§16, §29) */}
      <motion.div
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={handleClose}
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      />
      <motion.div
        className="relative z-10 bg-card border border-border/60 rounded-2xl shadow-2xl dark:bg-[#0f1011] dark:border-white/[0.08] p-6 w-full max-w-lg"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.97 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <h2
              className="text-foreground dark:text-[#f7f8f8]"
              style={{
                fontSize: '20px',
                fontWeight: 590,
                lineHeight: 1.33,
                letterSpacing: '-0.24px',
              }}
            >
              Upload materials
            </h2>
            <button
              onClick={handleClose}
              aria-label="Close upload dialog"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
            >
              <X className="h-4 w-4" weight="regular" />
            </button>
          </div>

          {/* Upload Type Tabs */}
          <div
            className="flex mb-5 p-1 bg-muted/50 dark:bg-white/[0.03] dark:border dark:border-white/[0.06] rounded-md gap-1"
            role="tablist"
            aria-label="Upload method"
            onKeyDown={(e) =>
              handleTabListKeyDown(e, {
                activeIndex: uploadType === 'file' ? 0 : 1,
                count: 2,
                onSelect: (i) => setUploadType(i === 0 ? 'file' : 'url'),
              })
            }
          >
            <button
              role="tab"
              id="upload-tab-file"
              aria-selected={uploadType === 'file'}
              aria-controls="upload-panel-file"
              tabIndex={uploadType === 'file' ? 0 : -1}
              className={cn(
                'flex-1 inline-flex items-center justify-center gap-2 py-1.5 px-4 rounded border transition-colors duration-150',
                uploadType === 'file'
                  ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]',
              )}
              style={{
                fontSize: '13px',
                fontWeight: uploadType === 'file' ? 590 : 510,
                letterSpacing: '-0.13px',
              }}
              onClick={() => setUploadType('file')}
            >
              <Upload className="h-4 w-4" weight="regular" />
              Upload files
            </button>
            <button
              role="tab"
              id="upload-tab-url"
              aria-selected={uploadType === 'url'}
              aria-controls="upload-panel-url"
              tabIndex={uploadType === 'url' ? 0 : -1}
              className={cn(
                'flex-1 inline-flex items-center justify-center gap-2 py-1.5 px-4 rounded border transition-colors duration-150',
                uploadType === 'url'
                  ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]',
              )}
              style={{
                fontSize: '13px',
                fontWeight: uploadType === 'url' ? 590 : 510,
                letterSpacing: '-0.13px',
              }}
              onClick={() => setUploadType('url')}
            >
              <LinkIcon className="h-4 w-4" weight="regular" />
              From URL
            </button>
          </div>

          {/* Tags: optional user-chosen tags applied to every file/URL in this
              batch. The backend still auto-tags the content type (pdf, docx,
              website, ...) on top of these. */}
          <div className="mb-4">
            <label
              htmlFor="upload-tags"
              className="block mb-1.5 text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              Tags (optional)
            </label>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/15"
                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px', lineHeight: 1.5 }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => setTags(prev => prev.filter(t => t !== tag))}
                      className="hover:opacity-70 transition-opacity duration-100"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X className="h-3 w-3" weight="regular" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              id="upload-tags"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault()
                  addTag()
                } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
                  setTags(prev => prev.slice(0, -1))
                }
              }}
              onBlur={addTag}
              placeholder="Type a tag and press Enter"
              className="w-full px-2.5 py-1.5 text-sm rounded-md bg-background border border-border/50 focus:outline-none focus:border-[#5e6ad2]/50 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:focus:border-[#5e6ad2]/70 dark:placeholder:text-[#62666d] transition-colors duration-150"
              style={{ letterSpacing: '-0.13px' }}
            />
          </div>

          {/* File Upload */}
          {uploadType === 'file' && (
            <div
              className="space-y-4"
              role="tabpanel"
              id="upload-panel-file"
              aria-labelledby="upload-tab-file"
            >
              {/* Drop zone — the copy promises "click to browse", so the whole
                  zone is the control: pointer, Enter and Space all open the
                  picker. The old inner "Browse files" button was removed so a
                  button is not nested inside this role="button". */}
              <div
                role="button"
                tabIndex={0}
                aria-label="Upload files — drag and drop, or activate to browse"
                className={cn(
                  'border border-dashed rounded-md p-8 text-center cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75',
                  dragActive
                    ? 'border-[#5e6ad2]/70 bg-[#5e6ad2]/[0.06] dark:border-[#7170ff]/70 dark:bg-[#5e6ad2]/[0.10]'
                    : 'border-border/60 hover:border-[#5e6ad2]/40 hover:bg-[#5e6ad2]/[0.04] dark:border-white/[0.10] dark:hover:border-[#7170ff]/40 dark:hover:bg-white/[0.02]'
                )}
                onClick={(e) => {
                  // The hidden input lives inside this zone, so its own
                  // programmatic click bubbles back here — ignore that echo.
                  if (e.target === fileInputRef.current) return
                  openFilePicker()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openFilePicker()
                  }
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                  <Upload className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                </div>
                <p
                  className="text-foreground dark:text-[#f7f8f8] mb-1"
                  style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
                >
                  Drag &amp; drop files here, or click to browse
                </p>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                >
                  PDF, DOC, DOCX, PPT, PPTX, XLSX, XLS, CSV, TXT, MD, up to 20 MB per file
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xlsx,.xls,.csv,.txt,.md"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    stageFiles(files)
                    e.target.value = ''
                  }}
                  className="hidden"
                  id="file-upload"
                />
              </div>

              {/* Staged files list with editable titles */}
              {stagedFiles.length > 0 && (
                <div className="space-y-3">
                  <p
                    className="text-muted-foreground dark:text-[#62666d]"
                    style={{
                      fontFamily: "'Berkeley Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: '10px',
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} ready · edit titles
                  </p>
                  <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                    {stagedFiles.map((sf, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 p-2.5 rounded-md bg-muted/40 border border-border/50 dark:bg-white/[0.02] dark:border-white/[0.06]"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98] flex-shrink-0" weight="regular" />
                        <input
                          type="text"
                          value={sf.title}
                          onChange={(e) => {
                            setStagedFiles(prev => prev.map((f, i) =>
                              i === idx ? { ...f, title: e.target.value } : f
                            ))
                          }}
                          className="flex-1 min-w-0 px-2.5 py-1 text-sm rounded bg-background border border-border/50 focus:outline-none focus:border-[#5e6ad2]/50 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:focus:border-[#5e6ad2]/70 dark:placeholder:text-[#62666d] transition-colors duration-150"
                          placeholder="Material title"
                          style={{ letterSpacing: '-0.13px' }}
                        />
                        <span
                          className="text-muted-foreground dark:text-[#62666d] flex-shrink-0 tabular-nums"
                          style={{ fontSize: '10px', letterSpacing: '-0.05px' }}
                        >
                          {(sf.file.size / (1024 * 1024)).toFixed(1)} MB
                        </span>
                        <button
                          type="button"
                          onClick={() => setStagedFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="p-1 rounded text-muted-foreground hover:text-destructive dark:text-[#8a8f98] dark:hover:text-red-400 transition-colors duration-150 flex-shrink-0"
                          aria-label="Remove file"
                        >
                          <X className="h-3.5 w-3.5" weight="regular" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="default"
                    onClick={handleUploadStaged}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" weight="regular" />
                    Upload {stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* URL Upload */}
          {uploadType === 'url' && (
            <div
              className="space-y-4"
              role="tabpanel"
              id="upload-panel-url"
              aria-labelledby="upload-tab-url"
            >
              <Input
                label="Website URL"
                placeholder="https://example.com/marketing-page"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                icon={<LinkIcon className="h-4 w-4" weight="regular" />}
              />
              <Input
                label="Title (optional)"
                placeholder="Marketing page title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                icon={<FileText className="h-4 w-4" weight="regular" />}
              />
              <Button
                variant="default"
                onClick={handleUrlUpload}
                disabled={!url.trim()}
                className="w-full"
              >
                <LinkIcon className="h-4 w-4 mr-2" weight="regular" />
                Upload from URL
              </Button>
            </div>
          )}
        </motion.div>
      </div>,
      document.body,
  )
}

// Material Card Component
const MaterialCard: React.FC<{
  material: Material;
  onDelete: (id: number) => void;
  onAnalyze: (id: number) => void;
  onPreview: (id: number) => void;
  onRename: (id: number, title: string) => void;
}> = ({ material, onDelete, onAnalyze, onPreview, onRename }) => {
  // Analyst holds materials.personal = 'view' (spec 4-G: views content,
  // edits nothing). Rename / Analyze / Delete all 403 for them server-side,
  // so don't offer them (AB#22372). Preview and Download stay.
  const { canWrite } = usePermission('materials', 'personal')
  const [menuCoords, setMenuCoords] = useState<{ top: number; right: number } | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(material.title)
  const showDropdown = menuCoords != null
  const closeMenu = () => setMenuCoords(null)

  React.useEffect(() => {
    if (!showDropdown) return
    const onDismiss = () => closeMenu()
    window.addEventListener('scroll', onDismiss, true)
    window.addEventListener('resize', onDismiss)
    return () => {
      window.removeEventListener('scroll', onDismiss, true)
      window.removeEventListener('resize', onDismiss)
    }
  }, [showDropdown])

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (showDropdown) { closeMenu(); return }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setMenuCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
  }

  const handleRenameSubmit = () => {
    const trimmed = editTitle.trim()
    if (trimmed && trimmed !== material.title) {
      onRename(material.id, trimmed)
    } else {
      setEditTitle(material.title)
    }
    setIsEditing(false)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit()
    } else if (e.key === 'Escape') {
      setEditTitle(material.title)
      setIsEditing(false)
    }
  }

  const getStatusIcon = () => {
    // Show embedding status when processing is done
    if (material.processing_status === 'completed') {
      if (material.embedding_status === 'processing') {
        return <ArrowsClockwise className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc] animate-spin" weight="regular" />
      }
      if (material.embedding_status === 'failed') {
        return <WarningCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" weight="regular" />
      }
      return <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" weight="regular" />
    }
    switch (material.processing_status) {
      case 'processing':
      case 'crawling':
      case 'indexing':
        return <ArrowsClockwise className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc] animate-spin" weight="regular" />
      case 'failed':
        return <WarningCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" weight="regular" />
      default:
        return <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" weight="regular" />
    }
  }

  const getStatusLabel = () => {
    if (material.processing_status === 'completed') {
      if (material.embedding_status === 'processing') return 'Embedding...'
      if (material.embedding_status === 'pending') return 'Pending embedding...'
      if (material.embedding_status === 'failed') return 'Embedding failed'
      return 'completed'
    }
    switch (material.processing_status) {
      case 'crawling': return 'Crawling website...'
      case 'indexing': return 'Indexing with PageIndex...'
      case 'processing': return 'Processing...'
      default: return material.processing_status
    }
  }

  const contentTypeMeta = (): { Icon: PhosphorIcon; iconClass: string; chipClass: string } => {
    switch (material.content_type) {
      case 'pdf':
        return {
          Icon: FilePdf,
          iconClass: 'text-red-600 dark:text-red-400',
          chipClass: 'bg-red-500/10 border-red-500/15 dark:bg-red-500/[0.10] dark:border-red-500/20',
        }
      case 'docx':
      case 'doc':
        return {
          Icon: FileDoc,
          iconClass: 'text-[#5e6ad2] dark:text-[#a5b4fc]',
          chipClass: 'bg-[#5e6ad2]/10 border-[#5e6ad2]/15 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/20',
        }
      case 'pptx':
      case 'ppt':
        return {
          Icon: FilePpt,
          iconClass: 'text-amber-600 dark:text-amber-400',
          chipClass: 'bg-amber-500/10 border-amber-500/15 dark:bg-amber-500/[0.10] dark:border-amber-500/20',
        }
      case 'website':
        return {
          Icon: Globe,
          iconClass: 'text-emerald-600 dark:text-emerald-400',
          chipClass: 'bg-emerald-500/10 border-emerald-500/15 dark:bg-emerald-500/[0.10] dark:border-emerald-500/20',
        }
      default:
        return {
          Icon: FileText,
          iconClass: 'text-muted-foreground dark:text-[#8a8f98]',
          chipClass: 'bg-muted/60 border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06]',
        }
    }
  }

  return (
    <motion.div
      className="relative"
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Keyboard affordance for the card. The Card itself can't take
          role="button" — it contains buttons (actions menu, rename controls),
          and that nesting is invalid. So a transparent stretched button sits
          over it instead.
          `pointer-events-none` is deliberate and load-bearing: it keeps this
          overlay out of mouse hit-testing, so the actions menu, the rename
          controls and the Card's own onClick all keep working, while the
          element stays in the tab order and Enter/Space still activate it.
          Do not "fix" it by removing that class. */}
      {!isEditing && (
        <button
          type="button"
          onClick={() => onPreview(material.id)}
          aria-label={`Preview ${material.title}`}
          className="absolute inset-0 z-0 rounded-lg pointer-events-none focus:outline-none focus-visible:border focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75"
        />
      )}
      <Card
        variant="default"
        className="h-full cursor-pointer border-border/50 rounded-lg transition-colors duration-200 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-[#7170ff]/25"
        onClick={() => !isEditing && onPreview(material.id)}
      >
        <CardHeader className="px-5 pt-5 pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {(() => {
                const { Icon, iconClass, chipClass } = contentTypeMeta()
                return (
                  <div className={cn('h-9 w-9 rounded-md flex items-center justify-center border flex-shrink-0', chipClass)}>
                    <Icon className={cn('h-[18px] w-[18px]', iconClass)} weight="regular" />
                  </div>
                )
              })()}
              {/* Title + status clamp to icon-chip height so the title's
                  baseline stays at the same y across every card, regardless
                  of how many bits live in the status row. */}
              <div className="flex-1 min-w-0 flex flex-col justify-center min-h-9">
                {isEditing ? (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={handleRenameKeyDown}
                      onBlur={handleRenameSubmit}
                      autoFocus
                      className="h-8 w-full rounded-md border bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50 hover:border-border/80 focus:outline-none focus-visible:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d] dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14] dark:focus-visible:border-[#7170ff]/75 px-2.5 transition-colors duration-150"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    />
                    <button
                      onClick={handleRenameSubmit}
                      className="h-7 w-7 flex items-center justify-center rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400 dark:hover:text-emerald-300 dark:hover:bg-emerald-500/[0.10] transition-colors duration-150"
                      aria-label="Save title"
                    >
                      <Check className="h-3.5 w-3.5" weight="bold" />
                    </button>
                  </div>
                ) : (
                  <h3
                    className="text-foreground dark:text-[#f7f8f8] truncate"
                    style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px', lineHeight: 1.25 }}
                  >
                    {material.title}
                  </h3>
                )}
                <div className="flex items-center gap-1.5 mt-1">
                  {getStatusIcon()}
                  <span
                    className="text-muted-foreground dark:text-[#8a8f98] capitalize"
                    style={{ fontSize: '12px', letterSpacing: '-0.05px', lineHeight: 1 }}
                  >
                    {getStatusLabel()}
                  </span>
                  {material.pageindex_doc_id && (
                    <>
                      <span className="text-muted-foreground/40 dark:text-[#3e3e44] select-none" aria-hidden="true">·</span>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/15"
                        style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.4 }}
                      >
                        Indexed
                      </span>
                    </>
                  )}
                  {material.crawl_pages_found && material.crawl_pages_found > 0 && (
                    <>
                      <span className="text-muted-foreground/40 dark:text-[#3e3e44] select-none" aria-hidden="true">·</span>
                      <span
                        className="text-muted-foreground/80 dark:text-[#62666d] tabular-nums"
                        style={{ fontSize: '12px', letterSpacing: '-0.05px', lineHeight: 1 }}
                      >
                        {material.crawl_pages_found} pages
                      </span>
                    </>
                  )}
                  {material.generation_source === 'ai_deck' && (
                    <>
                      <span className="text-muted-foreground/40 dark:text-[#3e3e44] select-none" aria-hidden="true">·</span>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/15"
                        style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.4 }}
                      >
                        AI
                      </span>
                    </>
                  )}
                  {/* Merge-field personalization is intentionally NOT badged
                      here — the "AI" chip is the only marker (stakeholder
                      preference); the per-recipient note lives in the
                      Collaterals node picker where it's actionable. */}
                </div>
              </div>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Material actions"
                onClick={toggleMenu}
              >
                <DotsThreeVertical className="h-4 w-4" weight="bold" />
              </Button>
              {showDropdown && menuCoords && createPortal(
                <>
                  {/* dismiss layer */}
                  <div
                    className="fixed inset-0 z-[110]"
                    onClick={closeMenu}
                    aria-hidden="true"
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    role="menu"
                    style={{ position: 'fixed', top: menuCoords.top, right: menuCoords.right }}
                    className="z-[111] w-48 rounded-lg bg-card border border-border/60 shadow-lg overflow-hidden py-1 dark:bg-[#191a1b] dark:border-white/[0.08]"
                  >
                    {canWrite && (
                      <button
                        role="menuitem"
                        type="button"
                        className="w-full flex items-center gap-2 px-2.5 py-2 mx-1 my-0.5 rounded-md text-foreground hover:bg-accent/40 dark:text-[#d0d6e0] dark:hover:bg-white/[0.04] transition-colors duration-100"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                        onClick={() => { closeMenu(); setEditTitle(material.title); setIsEditing(true) }}
                      >
                        <PencilSimple className="h-4 w-4" weight="regular" />
                        Rename
                      </button>
                    )}
                    <button
                      role="menuitem"
                      type="button"
                      className="w-full flex items-center gap-2 px-2.5 py-2 mx-1 my-0.5 rounded-md text-foreground hover:bg-accent/40 dark:text-[#d0d6e0] dark:hover:bg-white/[0.04] transition-colors duration-100"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                      onClick={() => { closeMenu(); onPreview(material.id) }}
                    >
                      <Eye className="h-4 w-4" weight="regular" />
                      Preview
                    </button>
                    {canWrite && (
                      <button
                        role="menuitem"
                        type="button"
                        className="w-full flex items-center gap-2 px-2.5 py-2 mx-1 my-0.5 rounded-md text-foreground hover:bg-accent/40 dark:text-[#d0d6e0] dark:hover:bg-white/[0.04] transition-colors duration-100"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                        onClick={() => { closeMenu(); onAnalyze(material.id) }}
                      >
                        <Robot className="h-4 w-4" weight="regular" />
                        Analyze
                      </button>
                    )}
                    {material.file_path && (
                      <button
                        role="menuitem"
                        type="button"
                        className="w-full flex items-center gap-2 px-2.5 py-2 mx-1 my-0.5 rounded-md text-foreground hover:bg-accent/40 dark:text-[#d0d6e0] dark:hover:bg-white/[0.04] transition-colors duration-100"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                        onClick={() => { closeMenu(); downloadMaterial(material.id, material.file_name) }}
                      >
                        <DownloadSimple className="h-4 w-4" weight="regular" />
                        Download
                      </button>
                    )}
                    {canWrite && (
                      <>
                        <div className="my-1 h-px bg-border/50 dark:bg-white/[0.06]" />
                        <button
                          role="menuitem"
                          type="button"
                          className="w-full flex items-center gap-2 px-2.5 py-2 mx-1 my-0.5 rounded-md text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/[0.08] transition-colors duration-100"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                          onClick={() => { closeMenu(); onDelete(material.id) }}
                        >
                          <Trash className="h-4 w-4" weight="regular" />
                          Delete
                        </button>
                      </>
                    )}
                  </motion.div>
                </>,
                document.body,
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-5 pt-0 pb-5">
          <div className="flex flex-col gap-4">
            {material.description && (
              <p
                className="text-muted-foreground dark:text-[#d0d6e0] line-clamp-2 break-words"
                style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
              >
                {material.description}
              </p>
            )}

            {material.processing_status === 'failed' && (
              <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-md bg-red-500/[0.07] border border-red-500/20 dark:bg-red-500/[0.10] dark:border-red-500/25">
                <WarningCircle className="h-4 w-4 mt-px flex-shrink-0 text-red-600 dark:text-red-400" weight="fill" />
                <p
                  className="text-red-700 dark:text-red-300 break-words"
                  style={{ fontSize: '12px', lineHeight: 1.5, letterSpacing: '-0.1px' }}
                >
                  {material.processing_error ||
                    'This file could not be processed. It may be corrupted or unreadable. Please re-save it and upload again.'}
                </p>
              </div>
            )}

            {sanitizeMaterialSummary(material.ai_summary) && (
              <div className="px-3.5 py-3 rounded-md bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/15">
                <p
                  className="text-[#5e6ad2] dark:text-[#a5b4fc] mb-2"
                  style={{
                    fontFamily: "'Berkeley Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: '10px',
                    fontWeight: 510,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  AI Summary
                </p>
                <p
                  className="text-foreground dark:text-[#d0d6e0] line-clamp-3"
                  style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
                >
                  {sanitizeMaterialSummary(material.ai_summary)}
                </p>
              </div>
            )}

            <div
              className="flex items-center justify-between text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1 }}
            >
              <span>{formatDate(material.created_at)}</span>
              {material.file_size && <span className="tabular-nums">{formatBytes(material.file_size)}</span>}
            </div>

            {material.tags && material.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {material.tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/15"
                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px', lineHeight: 1.5 }}
                  >
                    {tag}
                  </span>
                ))}
                {material.tags.length > 3 && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:text-[#8a8f98] border border-border/50 dark:border-white/[0.06]"
                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px', lineHeight: 1.5 }}
                  >
                    +{material.tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// Mono caption helper — Berkeley Mono editorial eyebrow used in this sidebar.
const sidebarEyebrow: React.CSSProperties = {
  fontFamily: "'Berkeley Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: '10px',
  fontWeight: 510,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
}

// Single-select view row (All Materials, Untagged).
const ViewRow: React.FC<{
  label: string
  count: number
  active: boolean
  icon: PhosphorIcon
  onClick: () => void
}> = ({ label, count, active, icon: Icon, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'w-full flex items-center gap-2 px-2.5 h-9 rounded-md transition-colors duration-150',
      active
        ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]'
        : 'text-foreground hover:bg-accent/40 dark:text-[#d0d6e0] dark:hover:bg-white/[0.04]'
    )}
    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
  >
    <Icon
      className={cn(
        'h-4 w-4 flex-shrink-0',
        active
          ? 'text-[#5e6ad2] dark:text-[#a5b4fc]'
          : 'text-muted-foreground dark:text-[#8a8f98]'
      )}
      weight={active ? 'fill' : 'regular'}
    />
    <span className="truncate flex-1 text-left">{label}</span>
    <span
      className={cn(
        'tabular-nums',
        active ? 'text-[#5e6ad2]/80 dark:text-[#a5b4fc]/80' : 'text-muted-foreground/80 dark:text-[#62666d]'
      )}
      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
    >
      {count}
    </span>
  </button>
)

// Multi-select tag filter row — leading checkbox makes the affordance obvious.
const TagFilterRow: React.FC<{
  tag: string
  count: number
  selected: boolean
  onToggle: () => void
}> = ({ tag, count, selected, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={selected}
    onClick={onToggle}
    className={cn(
      'w-full flex items-center gap-2.5 px-2.5 h-9 rounded-md transition-colors duration-150 group',
      selected
        ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]'
        : 'text-foreground hover:bg-accent/40 dark:text-[#d0d6e0] dark:hover:bg-white/[0.04]'
    )}
    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
  >
    {/* Custom checkbox — affordance: this is a multi-select filter, not a nav */}
    <span
      aria-hidden="true"
      className={cn(
        'flex items-center justify-center h-[14px] w-[14px] rounded-[3px] border transition-colors duration-150 flex-shrink-0',
        selected
          ? 'bg-[#5e6ad2] border-[#5e6ad2] dark:bg-[#7170ff] dark:border-[#7170ff]'
          : 'bg-transparent border-border/70 group-hover:border-border dark:border-white/[0.18] dark:group-hover:border-white/[0.28]'
      )}
    >
      {selected && <Check className="h-2.5 w-2.5 text-white" weight="bold" />}
    </span>
    <span className="truncate flex-1 text-left">{tag}</span>
    <span
      className={cn(
        'tabular-nums',
        selected ? 'text-[#5e6ad2]/80 dark:text-[#a5b4fc]/80' : 'text-muted-foreground/80 dark:text-[#62666d]'
      )}
      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
    >
      {count}
    </span>
  </button>
)

const FolderSidebar: React.FC<{
  materials: Material[]
  activeFolders: Set<string>
  onToggleFolder: (folder: string) => void
  onClearFolders: () => void
}> = ({ materials, activeFolders, onToggleFolder, onClearFolders }) => {
  const tagTree = useMemo(() => {
    const tagCounts: Record<string, number> = {}
    let untaggedCount = 0
    materials.forEach((m: Material) => {
      if (!m.tags || m.tags.length === 0) untaggedCount++
      else m.tags.forEach(t => { tagCounts[t] = (tagCounts[t] || 0) + 1 })
    })
    const sorted = Object.entries(tagCounts).sort((a, b) => b[1] - a[1])
    return { sorted, untaggedCount }
  }, [materials])

  const tagFiltersActive = useMemo(
    () => [...activeFolders].filter(f => f !== '__untagged__'),
    [activeFolders],
  )
  const untaggedActive = activeFolders.has('__untagged__')
  const allActive = activeFolders.size === 0
  const selectedTagCount = tagFiltersActive.length

  // Total filter checks = untagged + selected tags
  const totalActiveFilters = selectedTagCount + (untaggedActive ? 1 : 0)

  return (
    <div className="w-60 flex-shrink-0 border-r border-border/50 bg-muted/20 dark:border-white/[0.05] dark:bg-[#0c0d0e] overflow-y-auto">
      <div className="p-3 space-y-4">
        {/* ─── All materials anchor (single-select reset) ─── */}
        <div>
          <ViewRow
            label="All materials"
            count={materials.length}
            active={allActive}
            icon={FolderOpen}
            onClick={onClearFolders}
          />
        </div>

        {/* ─── Filter by tag (multi-select, Untagged folded in) ─── */}
        {(tagTree.sorted.length > 0 || tagTree.untaggedCount > 0) && (
          <div>
            <div className="flex items-center justify-between mb-2 px-2.5 gap-2">
              <p
                className="text-muted-foreground dark:text-[#8a8f98] truncate"
                style={sidebarEyebrow}
              >
                Filter by tag
              </p>
              {totalActiveFilters > 0 && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <span
                    className="inline-flex items-center justify-center rounded-full bg-[#5e6ad2]/15 text-[#5e6ad2] dark:text-[#a5b4fc] tabular-nums"
                    style={{
                      fontSize: '10px',
                      fontWeight: 590,
                      letterSpacing: '-0.1px',
                      padding: '1px 6px',
                      lineHeight: 1.4,
                    }}
                    aria-label={`${totalActiveFilters} filters active`}
                  >
                    {totalActiveFilters}
                  </span>
                  <button
                    type="button"
                    onClick={onClearFolders}
                    className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-150"
                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-0.5">
              {tagTree.untaggedCount > 0 && (
                <TagFilterRow
                  tag="Untagged"
                  count={tagTree.untaggedCount}
                  selected={untaggedActive}
                  onToggle={() => onToggleFolder('__untagged__')}
                />
              )}
              {tagTree.untaggedCount > 0 && tagTree.sorted.length > 0 && (
                <div className="h-px bg-border/40 dark:bg-white/[0.05] my-1.5 mx-2.5" aria-hidden="true" />
              )}
              {tagTree.sorted.map(([tag, count]) => (
                <TagFilterRow
                  key={tag}
                  tag={tag}
                  count={count}
                  selected={activeFolders.has(tag)}
                  onToggle={() => onToggleFolder(tag)}
                />
              ))}
            </div>
            <p
              className="text-muted-foreground/70 dark:text-[#3e3e44] mt-3 px-2.5"
              style={{ fontSize: '11px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
            >
              Tick multiple tags to combine filters.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

const MaterialsPage: React.FC = () => {
  // presentations.generate has no Analyst cell (deck generation spends
  // credits) — hide the entry point where the API would 403. UX-only; the
  // backend gate on POST /materials/generate-deck is the enforcement point.
  const canGenerateDeck = usePermission('presentations', 'generate').allowed
  // Uploading creates a material, so it needs write access on
  // materials.personal -- Analyst's 'view' does not qualify (AB#22372).
  const { canWrite: canUpload } = usePermission('materials', 'personal')
  const { isTrial, limits: trialLimits } = useTrialStatus()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [generateDeckModalOpen, setGenerateDeckModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [previewMaterialId, setPreviewMaterialId] = useState<number | null>(null)
  const [activeFolders, setActiveFolders] = useState<Set<string>>(new Set())
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)

  const toggleFolder = useCallback((folder: string) => {
    setActiveFolders(prev => {
      const next = new Set(prev)
      if (next.has(folder)) next.delete(folder)
      else next.add(folder)
      return next
    })
  }, [])

  const clearFolders = useCallback(() => {
    setActiveFolders(new Set())
  }, [])
  const queryClient = useQueryClient()

  // Fetch materials — poll while any material is still processing/embedding so the
  // UI flips from "Processing..." → "completed" without a manual refresh.
  const TRANSIENT_PROCESSING = new Set(['pending', 'processing', 'crawling', 'indexing'])
  const TRANSIENT_EMBEDDING = new Set(['pending', 'processing'])
  const { data: materials = [], isLoading, error } = useQuery({
    queryKey: ['materials'],
    queryFn: async () => {
      const response = await materialsApi.getAll()
      return response.data
    },
    refetchInterval: (data: Material[] | undefined) => {
      const list = data ?? []
      const anyTransient = list.some(
        (m) =>
          TRANSIENT_PROCESSING.has(m.processing_status) ||
          (m.processing_status === 'completed' &&
            m.embedding_status != null &&
            TRANSIENT_EMBEDDING.has(m.embedding_status))
      )
      return anyTransient ? 2500 : false
    },
  })

  // Surface background-processing failures (e.g. a corrupted file) to the user.
  // The upload call returns before processing runs, so a failure only shows up
  // later via polling; fire a toast the moment a material flips to 'failed'.
  // Only fresh transitions notify, not failures already present on load.
  const prevStatusRef = useRef<Record<number, string> | null>(null)
  useEffect(() => {
    const prev = prevStatusRef.current
    const next: Record<number, string> = {}
    for (const m of materials) next[m.id] = m.processing_status
    if (prev) {
      for (const m of materials) {
        if (m.processing_status === 'failed' && prev[m.id] && prev[m.id] !== 'failed') {
          toast.error(
            m.processing_error ||
              `"${m.title}" could not be processed. The file may be corrupted or unreadable.`,
            { duration: 7000 },
          )
        }
      }
    }
    prevStatusRef.current = next
  }, [materials])

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: materialsApi.delete,
    onSuccess: () => {
      toast.success('Material deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['materials'] })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete material'))
    }
  })

  // Analyze mutation
  const analyzeMutation = useMutation({
    mutationFn: materialsApi.analyze,
    onSuccess: () => {
      toast.success('Analysis started! This may take a few minutes.')
      queryClient.invalidateQueries({ queryKey: ['materials'] })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to start analysis'))
    }
  })

  const handleDelete = (id: number) => {
    setPendingDeleteId(id)
  }

  const confirmDelete = () => {
    if (pendingDeleteId == null) return
    const id = pendingDeleteId
    deleteMutation.mutate(id, {
      onSettled: () => setPendingDeleteId(null),
    })
  }

  const handleAnalyze = (id: number) => {
    analyzeMutation.mutate(id)
  }

  // Rename mutation
  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) =>
      materialsApi.update(id, { title }),
    onSuccess: () => {
      toast.success('Material renamed')
      queryClient.invalidateQueries({ queryKey: ['materials'] })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to rename material'))
    }
  })

  const handleRename = (id: number, title: string) => {
    renameMutation.mutate({ id, title })
  }

  // Filter by folder(s) and search
  const filteredMaterials = useMemo(() => {
    let result = materials as Material[]

    if (activeFolders.size > 0) {
      const hasUntagged = activeFolders.has('__untagged__')
      const tagFilters = [...activeFolders].filter(f => f !== '__untagged__')

      result = result.filter(m => {
        // Match untagged
        if (hasUntagged && (!m.tags || m.tags.length === 0)) return true
        // Match any selected tag
        if (tagFilters.length > 0 && m.tags?.some(t => tagFilters.includes(t))) return true
        return false
      })
    }

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(m =>
        m.title.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q)
      )
    }

    return result
  }, [materials, activeFolders, searchQuery])

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="h-12 w-12 rounded-md bg-red-100 dark:bg-red-500/10 dark:border dark:border-red-500/20 flex items-center justify-center mx-auto mb-4">
            <WarningCircle className="h-6 w-6 text-red-500" weight="regular" />
          </div>
          <h3
            className="text-foreground dark:text-[#f7f8f8] mb-2"
            style={{
              fontSize: '20px',
              fontWeight: 590,
              lineHeight: 1.33,
              letterSpacing: '-0.24px',
            }}
          >
            Failed to load materials
          </h3>
          <p className="text-sm text-muted-foreground dark:text-[#8a8f98] mb-5" style={{ letterSpacing: '-0.13px' }}>Please try again later</p>
          <Button
            variant="outline"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['materials'] })}
          >
            <ArrowsClockwise className="h-4 w-4 mr-2" weight="regular" />
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-6rem)] -mx-4 sm:-mx-6 -mt-6 sm:-mt-8 -mb-8">
      {/* Folder Sidebar */}
      {!isLoading && materials.length > 0 && (
        <FolderSidebar
          materials={materials}
          activeFolders={activeFolders}
          onToggleFolder={toggleFolder}
          onClearFolders={clearFolders}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 pb-3 border-b border-border/40 dark:border-white/[0.05]">
          <div className="min-w-0 flex items-center gap-2.5">
            <h1
              className="text-foreground dark:text-[#f7f8f8] truncate"
              style={{
                fontSize: '24px',
                fontWeight: 590,
                lineHeight: 1.33,
                letterSpacing: '-0.288px',
              }}
            >
              All materials
            </h1>
            <span
              className="inline-flex items-center justify-center flex-shrink-0 rounded-full bg-muted/60 border border-border/50 text-muted-foreground dark:bg-white/[0.04] dark:border-white/[0.08] dark:text-[#8a8f98] tabular-nums"
              style={{
                fontSize: '11px',
                fontWeight: 510,
                letterSpacing: '-0.1px',
                padding: '2px 8px',
                minWidth: '22px',
                lineHeight: 1.5,
              }}
              aria-label={`${filteredMaterials.length} items`}
            >
              {filteredMaterials.length}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canGenerateDeck && (
              <Button
                variant="outline"
                size="sm"
                icon={<Sparkle className="h-4 w-4" weight="fill" />}
                onClick={() => setGenerateDeckModalOpen(true)}
              >
                Generate with AI
              </Button>
            )}
            {canUpload && (
              <Button
                variant="default"
                size="sm"
                icon={<Upload className="h-4 w-4" weight="regular" />}
                onClick={() => setUploadModalOpen(true)}
              >
                Upload
              </Button>
            )}
          </div>
        </div>

        {/* Free-trial cap, proactive — only ever shown to a trial account. */}
        {isTrial && trialLimits && (
          <p
            className="text-muted-foreground dark:text-[#8a8f98]"
            style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
          >
            {materials.length} of {trialLimits.materials} materials used (free trial)
          </p>
        )}

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="flex-1 max-w-xl">
            <Input
              placeholder="Search materials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<MagnifyingGlass className="h-4 w-4" weight="regular" />}
            />
          </div>
        </div>

        {/* Active folder chips */}
        {activeFolders.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Showing:</span>
            {[...activeFolders].map(folder => (
              <span
                key={folder}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc] border border-[#5e6ad2]/20"
              >
                <Folder className="h-3 w-3" weight="regular" />
                {folder === '__untagged__' ? 'Untagged' : folder}
                <button
                  onClick={() => toggleFolder(folder)}
                  className="ml-0.5 hover:text-[#5e6ad2] dark:hover:text-[#a5b4fc] transition-colors duration-100"
                >
                  <X className="h-3 w-3" weight="regular" />
                </button>
              </span>
            ))}
            {activeFolders.size > 1 && (
              <button
                onClick={clearFolders}
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors duration-100 underline"
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {/* Materials Grid */}
        <div>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <MaterialCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredMaterials.length === 0 ? (
            <Card variant="default" className="border-border/50 rounded-lg dark:bg-white/[0.02] dark:border-white/[0.08]">
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                    {activeFolders.size > 0 ? (
                      <Folder className="h-6 w-6 text-muted-foreground" weight="regular" />
                    ) : (
                      <FileText className="h-6 w-6 text-muted-foreground" weight="regular" />
                    )}
                  </div>
                  <h3
                    className="text-foreground dark:text-[#f7f8f8] mb-2"
                    style={{
                      fontSize: '20px',
                      fontWeight: 590,
                      lineHeight: 1.33,
                      letterSpacing: '-0.24px',
                    }}
                  >
                    {searchQuery
                      ? 'No materials found'
                      : activeFolders.size > 0
                        ? 'No materials match these tags'
                        : 'No materials yet'
                    }
                  </h3>
                  <p className="text-sm text-muted-foreground dark:text-[#8a8f98] mb-6" style={{ letterSpacing: '-0.13px' }}>
                    {searchQuery
                      ? 'Try adjusting your search terms.'
                      : canUpload
                        ? 'Upload PDFs, presentations, and website links for AI analysis.'
                        : 'Materials your team uploads will show up here.'
                    }
                  </p>
                  {!searchQuery && canUpload && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setUploadModalOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" weight="regular" />
                      Upload Material
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredMaterials.map((material: Material) => (
                <MaterialCard
                  key={material.id}
                  material={material}
                  onDelete={handleDelete}
                  onAnalyze={handleAnalyze}
                  onPreview={(id) => setPreviewMaterialId(id)}
                  onRename={handleRename}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={() => {}}
      />

      {/* Generate with AI Modal */}
      <GenerateDeckModal
        isOpen={generateDeckModalOpen}
        onClose={() => setGenerateDeckModalOpen(false)}
      />

      {/* Material Preview Drawer */}
      <MaterialPreviewDrawer
        materialId={previewMaterialId}
        isOpen={previewMaterialId !== null}
        onClose={() => setPreviewMaterialId(null)}
        onDelete={(id) => {
          // The drawer confirms deletion via its own ConfirmDialog, so delete
          // directly here. Routing through handleDelete would open the
          // page-level ConfirmDialog too, showing the confirmation twice.
          setPreviewMaterialId(null)
          deleteMutation.mutate(id)
        }}
        onAnalyze={handleAnalyze}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDeleteId != null}
        title="Delete material"
        description="Are you sure you want to delete this material? This action cannot be undone."
        confirmLabel="Delete"
        tone="destructive"
        loading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  )
}

export default MaterialsPage